import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { FormConfig } from 'src/app/shared/models/dynamic.model';
import { ValidatorService } from 'src/app/shared/services/form-control-validators.service';
import { miscellaneousFormfieldsConfig } from '../../bag.config';
import { Observable } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { SaveBagMiscellaneous } from '../../store/bag.action';
import { IBagMiscellaneousData } from 'src/app/features/model/bag.model';
import {
  ActionIcons,
  LabelsAndPlaceHolders,
  ModalPopupMessages,
  ModeIcons,
  pageType,
  validationErrorMessages,
} from 'src/app/shared/utility/constants';
import { FloatLabelType } from '@angular/material/form-field';
import { BagService } from 'src/app/features/service/bag.service';
import { CommonGetScreenService } from 'src/app/shared/services/common-get-screen';
import { UserEffectivePermissionsService } from 'src/app/features/service/user-effective-permissions.service';
import { applyRpeFieldPermissionsToForm } from 'src/app/features/service/rpe-field-permission.helpers';
import {
  BAG_RPE_FIELD_REGISTRY,
} from '../../bag-rpe-field.registry';
import { BAG_RPE_MISCELLANEOUS_FORM_SCOPE, BAG_RPE_MODULE_NAMES } from '../../bag-rpe.constants';

@Component({
  selector: 'app-miscellaneous',
  templateUrl: './miscellaneous.component.html',
  styleUrl: './miscellaneous.component.scss',
})
export class MiscellaneousComponent implements OnInit {
  public bagMiscellaneousData$!: Observable<IBagMiscellaneousData>;
  public miscellaneousForm!: FormGroup;
  public miscellaneousFieldsJsonConfig!: FormConfig;
  public pageMode!: string;
  public modeIcons = ModeIcons;
  public misInfo = '';
  public isFrom = location.pathname.split('/')[1];
  public setMiscellaneousInfoWidth: any = {};
  public setMiscellaneousInfoErrorTooltip: any = {};
  public setMiscellaneousInfoTooltipEvent: any = {};

  private readonly destroyRef = inject(DestroyRef);
  private isBagInformationScreen = false;

  constructor(
    public commonService: CommonService,
    private readonly formUtilsService: ValidatorService,
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly bagService: BagService,
    private readonly getScreenService: CommonGetScreenService,
    private readonly userEffectivePermissionsService: UserEffectivePermissionsService
  ) {
    this.pageMode = ModeIcons.add;
    this.bagMiscellaneousData$ = this.store.select(
      state => state.bagMiscellaneous.miscellaneous
    );
  }

  ngOnInit(): void {
    this.miscellaneousFieldsJsonConfig = miscellaneousFormfieldsConfig;
    this.createForm();
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params.get(ModeIcons.mode) === ActionIcons.bulkEdit) {
        this.pageMode = ModeIcons.bulkEdit;
      } else if (params.get(ModeIcons.mode) === ActionIcons.edit) {
        this.pageMode = ModeIcons.edit;
      } else if (
        params.get(ModeIcons.mode) === ActionIcons.view ||
        params.get(ModeIcons.mode) === ActionIcons.bulkview
      ) {
        this.pageMode = ModeIcons.view;
      }
    });
    this.dataFetch();
    this.commonService.miscellaneousInvalid
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === true) {
          this.miscellaneousForm.markAllAsTouched();
        }
      });
    this.getPageMode();
    this.userEffectivePermissionsService.subscribeLocationRefresh(
      this.destroyRef,
      () => this.applyRpeFieldPermissions()
    );
  }

  private applyRpeFieldPermissions(): void {
    if (!this.isBagInformationScreen || !this.miscellaneousForm) {
      return;
    }
    applyRpeFieldPermissionsToForm(
      this.userEffectivePermissionsService,
      BAG_RPE_MODULE_NAMES,
      this.miscellaneousForm,
      BAG_RPE_FIELD_REGISTRY,
      this.pageMode,
      BAG_RPE_MISCELLANEOUS_FORM_SCOPE
    );
  }

  public createForm() {
    const { form } = this.formUtilsService.generateForm(
      this.miscellaneousFieldsJsonConfig
    , this.destroyRef);
    this.miscellaneousForm = form;
  }

  public dispatchMiscellaneousFormValueChanges() {
    this.store.dispatch(new SaveBagMiscellaneous(this.miscellaneousForm.value));
    const tareWeightvalue = this.miscellaneousForm.get('tareWeight')?.value;
    if (
      tareWeightvalue === '0' ||
      tareWeightvalue === '0.' ||
      tareWeightvalue === '0.0' ||
      tareWeightvalue === '0.00'
    ) {
      this.miscellaneousForm
        .get('tareWeight')
        ?.setErrors({ invalidTareWeight: true });
    }
    this.bagService.isBagMiscellaneousValid(
      this.getErrorMessage('tareWeight') === ''
    );
    this.commonService.checkPristineAccordionData(
      this.miscellaneousForm.pristine
    );
  }

  public getErrorMessage(fieldId: string): string {
    if (this.miscellaneousForm.get(fieldId)?.errors?.['invalidTareWeight']) {
      return validationErrorMessages.invalidTareWeight;
    }
    return '';
  }

  public checkControlHasError(controlName: string) {
    return this.commonService.checkControlError(
      this.miscellaneousForm,
      controlName
    );
  }

  public getMiscellaneousPlaceholder(controlName: string): string {
    const bulkBagInfoData = this.bagService.getBulkBagInfoData();
    const hasBulkData = (bulkBagInfoData?.bagIds?.length ?? 0) > 1;
    if (hasBulkData) {
      const bulkValue = bulkBagInfoData?.miscellaneous?.[controlName];
      if (bulkValue === null) {
        return '';
      }
      if (bulkValue === '' || bulkValue === undefined) {
        return LabelsAndPlaceHolders.variousValues;
      }
    }
    return this.commonService.getPlaceholder(
      this.miscellaneousForm,
      controlName,
      this.pageMode,
      ModeIcons,
      bulkBagInfoData?.miscellaneous
    );
  }

  public getFloatLabelForBulkEdit(): FloatLabelType {
    const bulkBagInfoData = this.bagService.getBulkBagInfoData();
    const hasBulkData = (bulkBagInfoData?.bagIds?.length ?? 0) > 1;
    return hasBulkData ? LabelsAndPlaceHolders.floatLabelAlways : LabelsAndPlaceHolders.floatLabelAuto;
  }

  public fetchWidth(event: MouseEvent, fieldName: string) {
    this.commonService.setErrorTooltipData(
      event,
      fieldName,
      this.setMiscellaneousInfoWidth,
      this.setMiscellaneousInfoTooltipEvent,
      this.setMiscellaneousInfoErrorTooltip
    );
  }

  private dataFetch() {
    this.commonService.savedBagData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === pageType.dataBind) {
          this.fetchOldValues();
        }
      });
  }

  private fetchOldValues() {
    this.bagMiscellaneousData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
      this.miscellaneousForm.patchValue(data);
      this.applyRpeFieldPermissions();
    });
  }

  private getPageMode() {
    this.commonService.isBagPageMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string) => {
        this.checkScreen();
        if (this.commonService.isValidField(data)) {
          this.pageMode = data;
          if (this.pageMode === ModeIcons.view) {
            this.miscellaneousForm.disable();
          } else {
            this.miscellaneousForm.enable();
            this.applyRpeFieldPermissions();
          }
        }
      });
  }


  private checkScreen() {
    const location = window.location.pathname;
    const screen = this.getScreenService.getScreenFromUrl(
      this.isFrom,
      location
    );
    if (screen === pageType.bag) {
      this.isBagInformationScreen = true;
      this.misInfo = ModalPopupMessages.bagMiscellaneous;
    } else {
      this.isBagInformationScreen = false;
      this.misInfo = ModalPopupMessages.loreum;
    }
  }
}
